import { makeAutoObservable, runInAction } from 'mobx'
import moment from 'moment'
import { injectable, inject, decorate } from 'inversify'

// evertel
import { Api } from '@evertel/api'
import { APIDataDepartment } from '@evertel/types'
import { getAbbreviatedName } from '@evertel/utils'
import { DepartmentStore, APIDataDepartmentExtended } from '../store'


export interface DepartmentCapacities {
    verifiedMemberCapacity: number
    verifiedMembersCount: number
    verifiedMembersAvailable: number
}

export type DepartmentStatus = 'active' | 'in_trial' | 'trial_expired' | 'churned' | undefined

/**
 * Determines the trial or subscription status of a department.
 *
 * @param department - The department object containing trial and subscription information.
 * @returns The status of the department.
 */
export function getDepartmentTrialStatus(department: APIDataDepartment): DepartmentStatus {
    const today = moment()
    const expirationDate = department.subscriptionExpiresDate ? moment(department.subscriptionExpiresDate) : null
    const hasExpired = !expirationDate ?  true : today.isAfter(expirationDate, 'day')

    if (department.isTrial) {
        return hasExpired ? 'trial_expired' : 'in_trial'
    }

    if (expirationDate) {
        return hasExpired ? 'churned' : 'active'
    }

    // Handle case where there's no trial and no expiration date
    return undefined
}


class DepartmentController {
    departmentId = 0
    capacities: DepartmentCapacities = undefined

    constructor(
        private api: Api,
        private departmentStore: DepartmentStore
    ) {
        makeAutoObservable(this)
    }

    async init(departmentId: number, filter?: any) {
        if (!departmentId) return

        this.departmentId = departmentId

        // If already in store with real data, done
        const existing = this.departmentStore.findById(departmentId)
        if (existing?.id) return

        await this.fetchDepartment(filter)
    }

    private get defaultFilter() {
        return { include: ['publicMedia'] }
    }

    fetchDepartment = async (filter?: any, departmentId?: number): Promise<APIDataDepartment> => {
        const targetId = departmentId || this.departmentId
        if (!targetId) return

        // If another controller is already fetching this department, wait for that request
        const inflight = this.departmentStore.getInflightFetch(targetId)
        if (inflight) {
            await inflight
            return this.departmentStore.findById(targetId) as APIDataDepartment
        }

        // Start fetch and register so other callers can coalesce onto it
        const promise = this.api.Routes.Department.getById(targetId, {
            ...this.defaultFilter,
            ...filter
        })
            .then((department) => {
                this.departmentStore.update(department as APIDataDepartment)
                return department as APIDataDepartment
            })
            .finally(() => {
                this.departmentStore.clearInflightFetch(targetId)
            })

        this.departmentStore.setInflightFetch(targetId, promise)

        return await promise
    }

    /**
     * Fetches and hydrates the DepartmentStore with full department records
     * for the given IDs. Skips any departments already present in the store.
     * Uses a single batch API call for all missing departments.
     */
    hydrateDepartmentsById = async (departmentIds: number[]) => {
        if (!departmentIds?.length) return

        const missingIds = departmentIds.filter(id => {
            const existing = this.departmentStore.findById(id)
            return !existing || !existing.id
        })

        if (!missingIds.length) return

        try {
            const departments = await this.api.Routes.Department.get({
                ...this.defaultFilter,
                where: { id: { inq: missingIds } }
            })

            this.departmentStore.update(departments as APIDataDepartment[])
        } catch (e) {
            /** nothing — callers can still attempt individual fetches as fallback */
        }
    }

    /**
     * Force re-fetches a department from the API and updates the store,
     * regardless of whether it already exists locally.
     * Defaults to the current departmentId if none is provided.
     */
    refreshDepartment = async (departmentId?: number) => {
        await this.fetchDepartment(undefined, departmentId || this.departmentId)
    }

    fetchAvailableCapacities = async () => {
        const capacities = await this.api.Routes.Department.getAvailableCapacities(this.departmentId) as DepartmentCapacities

        runInAction(() => {
            this.capacities = capacities
        })
    }

    get id() {
        return this.departmentId
    }

    get department(): APIDataDepartmentExtended {
        const dept = this.departmentStore.findById(this.departmentId)
        return dept || {}
    }

    get shortName() {
        if (this.department?.shortName) return this.department.shortName
        return getAbbreviatedName(this.department?.name)
    }

    get integrationsEnabled(): boolean {
        return !!this.department?.options?.integrationsEnabled
    }

}

decorate(injectable(), DepartmentController)
decorate(inject(Api), DepartmentController, 0)
decorate(inject(DepartmentStore), DepartmentController, 1)

export { DepartmentController }
